import axios from 'axios';

import { COREAPI, getUserData, onLogout } from '../../../constants/global';
import { pageurl } from '../../../constants/pageurl';
import type { IActivityDataState, ILogin, IProfileResponse } from '../../../interface/state/IAuthState';
import { extractApiErrorMessage } from '../../../lib/api-error';
import { authType, globalType } from '../../types';
import * as utils from '../utils';
import type { IResponse } from './election';

export const login_Auth = ({
	onFailure,
	onSuccess,
	data,
}: IResponse<ILogin> & { data: { username: string; password: string } }) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/login',
			header: utils.header(),
			data,
		},
		actionType: authType.login_Auth,
		onSuccess: (res: ILogin) => {
			onSuccess?.(res);
			localStorage.setItem('userData', JSON.stringify(res));
		},
		onFailure,
		auth: true,
	});

/**
 * Step 1 of the email + OTP sign-in: asks api.core to send a code.
 *
 * <p>api.core answers 409 while the account still has an active session elsewhere
 * (`AppUser.IsLoggedIn`). Closing a browser does not clear that flag, so this is an ordinary state,
 * not an error: it goes to `onConflict` with no toast, and the screen asks whether to sign that
 * session out. Every other failure is reported via `onFailure` with the server's message.</p>
 */
export const userFacingLogin_Auth =
	({ data, onFailure, onSuccess, onConflict }: IResponse & { data: { email: string }; onConflict?: () => void }) =>
	async (dispatch: any) => {
		dispatch({ type: authType.userFacingLogin_Auth.dataLoading, payload: true });
		try {
			const res = await axios({
				url: `${COREAPI}/api/v1/Auth/userfacinglogin`,
				method: 'post',
				data,
				timeout: 30000,
				validateStatus: () => true,
				headers: utils.header(),
			});

			if (res.status >= 200 && res.status < 300) {
				dispatch({ type: authType.userFacingLogin_Auth.dataAction, payload: res.data });
				onSuccess?.(undefined);
			} else if (res.status === 409) {
				onConflict?.();
			} else {
				const notice = extractApiErrorMessage(res.data) || 'Your sign-in code could not be sent. Try again.';
				dispatch({ type: globalType.notificationGlobal.dataAction, payload: { notice, status: false } });
				onFailure?.({ notice, status: false });
			}
		} catch (error) {
			if (!axios.isCancel(error)) {
				const notice = 'iElect could not be reached. Check your connection and try again.';
				dispatch({ type: globalType.notificationGlobal.dataAction, payload: { notice, status: false } });
				onFailure?.({ notice, status: false });
			}
		} finally {
			dispatch({ type: authType.userFacingLogin_Auth.dataLoading, payload: false });
		}
	};

export const otpLogin_Auth =
	({ data, onSuccess, onFailure, _retried }: IResponse<ILogin> & { data: { email: string; otp: string }; _retried?: boolean }) =>
	async (dispatch: any) => {
		dispatch({ type: authType.otpLogin_Auth.dataLoading, payload: true });
		let retrying = false;
		try {
			const res = await axios({
				url: `${COREAPI}/api/v1/Auth/loginwithotp`,
				method: 'post',
				data,
				timeout: 10000 * 30,
				validateStatus: () => true,
				headers: utils.header(),
			});

			if (res.status >= 200 && res.status < 300) {
				dispatch({ type: authType.otpLogin_Auth.dataAction, payload: res.data });
				onSuccess?.(res.data);
			} else if (res.status === 409 && !_retried) {
				retrying = true;
				const email = res.data?.email || data.email;
				dispatch(
					logoutByEmail_Auth({
						data: { email },
						onSuccess: () => {
							dispatch(otpLogin_Auth({ data, onSuccess, onFailure, _retried: true }));
						},
						onFailure: () => {
							dispatch({
								type: globalType.notificationGlobal.dataAction,
								payload: { notice: 'Failed to log out existing session. Please try again.', status: false },
							});
							dispatch({ type: authType.otpLogin_Auth.dataLoading, payload: false });
							onFailure?.({ notice: 'Failed to log out existing session', status: false });
						},
					})
				);
			} else {
				// Refusals arrive as ProblemDetails (title + detail, no `message`): a wrong code, a
				// deactivated account, and the Independent Supporter refusals ("Supporter account
				// suspended", …) whose title and detail the sign-in screen shows word for word.
				const body = res.data && typeof res.data === 'object' ? res.data : {};
				const title = typeof body.title === 'string' ? body.title.trim() : '';
				const detail = typeof body.detail === 'string' ? body.detail.trim() : '';
				const message =
					(typeof body.message === 'string' && body.message) ||
					(title && detail ? `${title}. ${detail}` : extractApiErrorMessage(body)) ||
					'Something went wrong';
				dispatch({
					type: globalType.notificationGlobal.dataAction,
					payload: { notice: message, status: false },
				});
				onFailure?.({ notice: message, status: false, httpStatus: res.status, title, detail });
			}
		} catch (error) {
			if (!axios.isCancel(error)) {
				dispatch({
					type: globalType.notificationGlobal.dataAction,
					payload: { notice: 'Something went wrong', status: false },
				});
				onFailure?.({ notice: 'Something went wrong', status: false });
			}
		} finally {
			if (!retrying) {
				dispatch({ type: authType.otpLogin_Auth.dataLoading, payload: false });
			}
		}
	};

export interface IRegister {
	firstName: string;
	lastName: string;
	email: string;
	password: string;
	phoneNumber: string;
	role: string[];
}

export const register_Auth = ({ data, onSuccess, onFailure }: IResponse & { data: IRegister }) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/register',
			header: utils.header(),
			data,
		},
		actionType: authType.register_Auth,
		onSuccess,
		onFailure,
		auth: true,
	});

export interface IUpdateUser {
	email: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
}

export const update_Auth = ({
	data,
	userId,
	onSuccess,
	onFailure,
}: IResponse & {
	data: IUpdateUser;
	userId: string;
}) =>
	utils.httpPutMethod({
		apiData: {
			url: `/api/v1/User/update/${userId}`,
			header: utils.header(),
			data,
		},
		actionType: authType.update_Auth,
		onSuccess,
		onFailure,
		auth: true,
	});

export const changePassword_Auth = ({
	data,
	onSuccess,
	onFailure,
}: IResponse & {
	data: {
		currentPassword: string;
		newPassword: string;
	};
}) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/Auth/changepassword',
			header: utils.header(),
			data,
		},
		actionType: authType.changePassword_Auth,
		onSuccess,
		onFailure,
		auth: true,
	});

export const logout_Auth = ({ onSuccess, onFailure }: IResponse) =>
	utils.httpGetMethod({
		apiData: {
			url: '/api/v1/Auth/logout',
			header: utils.header(),
		},
		actionType: authType.logout_Auth,
		onSuccess: () => {
			onLogout();
			window.open(pageurl.LOGIN, '_self');
		},
		onFailure: () => {
			onLogout();
			window.open(pageurl.LOGIN, '_self');
		},
		auth: true,
	});

/**
 * Ends every session for an email (`POST /Auth/logout/email`, anonymous by design).
 *
 * <p>Quiet: no global toast on failure. Every caller already says what went wrong in its own words
 * (the login screen inline, the OTP retry with its own toast, verify-otp by returning to login),
 * and the shared helper's generic "Something went wrong" toast landed on top of those messages.</p>
 */
export const logoutByEmail_Auth =
	({ data, onSuccess, onFailure }: IResponse & { data: { email: string } }) =>
	async (dispatch: any) => {
		dispatch({ type: authType.logoutByEmail_Auth.dataLoading, payload: true });
		try {
			const res = await axios({
				url: `${COREAPI}/api/v1/Auth/logout/email`,
				method: 'post',
				data,
				timeout: 30000,
				validateStatus: () => true,
				headers: { 'Content-Type': 'application/json-patch+json' },
			});

			if (res.status >= 200 && res.status < 300) {
				dispatch({ type: authType.logoutByEmail_Auth.dataAction, payload: res.data });
				onSuccess?.(res.data);
			} else {
				onFailure?.({ notice: extractApiErrorMessage(res.data) || 'Sign-out failed', status: false });
			}
		} catch (error) {
			if (!axios.isCancel(error)) onFailure?.({ notice: 'Sign-out failed', status: false });
		} finally {
			dispatch({ type: authType.logoutByEmail_Auth.dataLoading, payload: false });
		}
	};

export const activityData_Auth = ({ onSuccess, onFailure }: IResponse<IActivityDataState>) =>
	utils.httpGetMethod({
		apiData: {
			url: '/api/v1/Auth/activitydata',
			header: utils.header(),
		},
		actionType: authType.activityData_Auth,
		onSuccess,
		onFailure,
		auth: true,
	});

export const getProfile_Auth = ({ onSuccess, onFailure }: IResponse<IProfileResponse>) =>
	utils.httpPostMethod({
		apiData: {
			url: '/api/v1/User/GetUserByEmail',
			header: utils.header(),
			data: { email: getUserData()?.user?.Email || '' },
		},
		actionType: authType.getProfile_Auth,
		onSuccess,
		onFailure,
		auth: true,
	});
